import { useQuery } from '@tanstack/react-query'

import { useDevicesApi } from '../api/context'
import { DEVICE_QUERY_KEYS } from '../api/endpoints'
import type {
  ProvisioningDirectoryItem,
  ProvisioningUserItem,
  SystemDefaultsItem,
} from '../types/device.types'
import { toApiError } from '../utils/formatApiError'

export function useGetSystemDefaults(pbxId: number) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.systemDefaults(pbxId),
    queryFn: async () => {
      const response = await api.getSystemDefaults(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch system defaults')
      }
      return response.data as SystemDefaultsItem
    },
    enabled: !!pbxId && pbxId > 0,
  })
}

export function useGetExtensionsLine(pbxId: number) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.extensionsLine(pbxId),
    queryFn: async () => {
      const response = await api.getExtensionLines(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch extensions')
      }
      const data = response.data
      const rawList = Array.isArray(data)
        ? (data as Record<string, unknown>[])
        : ((data as { results?: Record<string, unknown>[] })?.results ?? [])

      // Match legacy Provisioning::get_line_extensions_by_user_id, which only
      // returns active extensions (e.active = 1) for the device form. The app
      // v1 endpoint does not expose this as a query filter, so we filter on
      // the client. `active` may arrive as 0/1, true/false, or be absent on
      // older payloads — only drop rows we explicitly know are inactive.
      const filtered = rawList.filter((ext) => {
        const active = (ext as { active?: unknown }).active
        if (active === undefined || active === null) return true
        if (typeof active === 'boolean') return active
        if (typeof active === 'number') return active !== 0
        if (typeof active === 'string') return active !== '0' && active !== ''
        return true
      })

      return filtered as {
        id: number
        number: string
        name: string
        active?: number | boolean
      }[]
    },
    enabled: !!pbxId && pbxId > 0,
  })
}

export function useGetExtensionsSharedVoicemailBox(pbxId: number) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.extensionsSharedVoicemailBox(pbxId),
    queryFn: async () => {
      const response = await api.getSharedVoicemailBoxes(pbxId)
      if (!response.ok) {
        throw toApiError(
          response.data,
          'Failed to fetch shared voicemail extensions',
        )
      }
      const data = response.data
      if (Array.isArray(data))
        return data as { id: number; number: string; name: string }[]
      return (
        (data as { results: { id: number; number: string; name: string }[] })
          ?.results ?? []
      )
    },
    enabled: !!pbxId && pbxId > 0,
  })
}

export function useGetUsers(pbxId: number) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.users(pbxId),
    queryFn: async () => {
      const response = await api.getUsers(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch users')
      }
      const data = response.data
      const rawUsers = Array.isArray(data)
        ? data
        : ((data as { results: ProvisioningUserItem[] })?.results ?? [])

      return rawUsers.map((user: Record<string, unknown>) => {
        const source = user as Record<string, unknown>
        const fullName =
          (source.full_name as string) ||
          (source.name as string) ||
          [source.first_name, source.last_name].filter(Boolean).join(' ') ||
          `User ${user.id}`
        return {
          id: user.id as number,
          full_name: fullName,
          username: source.username as string | undefined,
        }
      })
    },
    enabled: !!pbxId && pbxId > 0,
  })
}

export function useGetDirectories(pbxId: number) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.directories(pbxId),
    queryFn: async () => {
      const response = await api.getDirectories(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch directories')
      }
      const data = response.data
      if (Array.isArray(data)) return data as ProvisioningDirectoryItem[]
      return (data as { results: ProvisioningDirectoryItem[] })?.results ?? []
    },
    enabled: !!pbxId && pbxId > 0,
  })
}
